/** @jsxImportSource theme-ui */
import { Box } from 'theme-ui';
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/solid';
import DataTable from '@/components/DataTable';
import { CompanyService, CompanyServicePrice } from '@/types/types';
import {
    formatPriceValue,
    formatValidDays,
    formatValidityDates,
    formatValidTimeRange,
    getServicePrices,
    ServicePricesState,
    summarizeServicePrices,
} from './servicePrices';
import { MISSING_VALUE, ValueOrMissingCell } from './cells';

export const PRICES_LOADING_TEXT = 'Loading…';
export const PRICES_UNAVAILABLE_TEXT = 'Could not load prices';

type PriceRowCellProps = { row: { original: CompanyServicePrice } };

export type ExpandableServiceRow = {
    original: CompanyService;
    isExpanded?: boolean;
    getToggleRowExpandedProps: (props?: object) => object;
};

type ServicePricesCellProps = {
    row: ExpandableServiceRow;
    servicePrices: ServicePricesState;
};

export const ServicePricesCell = ({ row, servicePrices }: ServicePricesCellProps) => {
    if (servicePrices.isLoading) return <>{PRICES_LOADING_TEXT}</>;
    if (servicePrices.hasError) return <>{PRICES_UNAVAILABLE_TEXT}</>;

    const prices = getServicePrices(servicePrices, row.original.Id);

    if (!prices.length) return <>{MISSING_VALUE}</>;

    return <ServicePricesToggle row={row} prices={prices} />;
};

const ServicePricesToggle = ({
    row,
    prices,
}: {
    row: ExpandableServiceRow;
    prices: CompanyServicePrice[];
}) => {
    const ChevronIcon = row.isExpanded ? ChevronDownIcon : ChevronRightIcon;
    const toggleLabel = `${row.isExpanded ? 'Hide' : 'Show'} prices of ${row.original.Name}`;

    return (
        <button
            type="button"
            {...row.getToggleRowExpandedProps({ title: toggleLabel })}
            aria-label={toggleLabel}
            aria-expanded={Boolean(row.isExpanded)}
            sx={toggleSx}
        >
            <ChevronIcon width={16} height={16} aria-hidden />
            {summarizeServicePrices(prices)}
        </button>
    );
};

const toggleSx = {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    padding: 0,
    border: 'none',
    background: 'none',
    color: 'inherit',
    font: 'inherit',
    cursor: 'pointer',
} as const;

export const FoldedServicePrices = ({ row, servicePrices }: ServicePricesCellProps) => {
    const prices = getServicePrices(servicePrices, row.original.Id);

    return (
        <Box
            role="region"
            aria-label={`Prices of ${row.original.Name}`}
            sx={{ paddingY: 2, paddingLeft: 4 }}
        >
            <DataTable data={prices} columns={priceColumns as any} />
        </Box>
    );
};

const priceColumns = [
    {
        id: 'Price',
        columnKey: 'Price',
        Header: 'Price',
        accessor: (price: CompanyServicePrice) => price.Price,
        Cell: ({ row }: PriceRowCellProps) => <>{formatPriceValue(row.original)}</>,
    },
    { columnKey: 'Category', Header: 'Category', accessor: 'Category', Cell: ValueOrMissingCell },
    {
        id: 'ValidDates',
        columnKey: 'ValidDates',
        Header: 'Valid dates',
        accessor: (price: CompanyServicePrice) => price.From,
        Cell: ({ row }: PriceRowCellProps) => (
            <>{formatValidityDates(row.original.From, row.original.To)}</>
        ),
    },
    {
        id: 'ValidDays',
        columnKey: 'ValidDays',
        Header: 'Days',
        accessor: (price: CompanyServicePrice) => formatValidDays(price),
    },
    {
        id: 'ValidTime',
        columnKey: 'ValidTime',
        Header: 'Time',
        accessor: (price: CompanyServicePrice) => formatValidTimeRange(price) ?? 'all day',
    },
    { columnKey: 'VAT', Header: 'VAT (%)', accessor: 'VAT', Cell: ValueOrMissingCell },
];
